// The offer calculator's core: prices, delivery times, the shareable offer link, QR codes and the PDF.
// No DOM in here, so the same code runs in the shop and in the unit tests (tests/unit/angebot.test.mjs).
(root => {
  'use strict';

  // ---------------------------------------------------------------------------------------------------------------
  // Money and numbers. Everything is counted in whole cents.

  // "4,90", "4,90 €", "1.234,50" or "4.90" to cents; blank or unreadable gives null.
  const parseMoney = value => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value * 100) : null;
    let text = String(value).replace(/[^\d,.-]/g, '');
    if (!text) return null;
    if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
    else if ((text.match(/\./g) || []).length > 1 || /\.\d{3}$/.test(text)) text = text.replace(/\./g, '');
    const number = Number.parseFloat(text);
    return Number.isFinite(number) ? Math.round(number * 100) : null;
  };

  const parseNumber = (value, fallback = 0) => {
    const number = Number.parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(number) ? number : fallback;
  };

  // 123456 -> "1.234,56 €"; German grouping written out so the screen and the PDF always match.
  const formatMoney = (cents, { sign = false, currency = true } = {}) => {
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));
    const euros = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const rest = String(abs % 100).padStart(2, '0');
    const prefix = negative ? '− ' : sign ? '+ ' : '';
    return `${prefix}${euros},${rest}${currency ? ' €' : ''}`;
  };

  const formatCount = count => String(Math.round(count)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  const formatPercent = value => `${String(Math.round(value * 10) / 10).replace('.', ',')} %`;

  // ---------------------------------------------------------------------------------------------------------------
  // Dates. Delivery times are counted in working days (Monday to Friday); public holidays are left to the "ca.".

  const pad = number => String(number).padStart(2, '0');
  const isoDate = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const parseDate = text => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(text || ''));
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) || isoDate(date) !== match[0] ? null : date;
  };
  const formatDate = date => `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  const startOfDay = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const isWorkday = date => date.getDay() !== 0 && date.getDay() !== 6;

  const addWorkdays = (from, days) => {
    const date = startOfDay(from);
    let left = Math.max(0, Math.round(days));
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      if (isWorkday(date)) left -= 1;
    }
    return date;
  };

  // Working days after `from` up to and including `to`.
  const workdaysBetween = (from, to) => {
    const date = startOfDay(from);
    const end = startOfDay(to);
    let count = 0;
    while (date < end) {
      date.setDate(date.getDate() + 1);
      if (isWorkday(date)) count += 1;
    }
    return count;
  };

  const addDays = (from, days) => {
    const date = startOfDay(from);
    date.setDate(date.getDate() + days);
    return date;
  };

  // ---------------------------------------------------------------------------------------------------------------
  // Configuration. The section hands over the raw settings; this makes them safe to calculate with.

  // "Menge | Wert" lines, e.g. "50 | 15" -> [[50, 15]], sorted by amount, the first starting at 1.
  const parseSteps = text => {
    const steps = String(text || '')
      .split(/\r?\n/)
      .map(line => line.split('|').map(part => part.trim()))
      .filter(parts => parts.length >= 2 && parts[0] !== '')
      .map(([from, value]) => [Math.max(1, Math.round(parseNumber(from, 1))), parseNumber(value, 0)])
      .sort((a, b) => a[0] - b[0]);
    return steps.length && steps[0][0] === 1 ? steps : [[1, 0], ...steps];
  };

  const stepFor = (steps, count) => {
    let found = steps[0] || [1, 0];
    steps.forEach(step => {
      if (count >= step[0]) found = step;
    });
    return found;
  };

  const nextStep = (steps, count) => steps.find(step => step[0] > count) || null;

  // "Code | Land | Versand pro Paket | USt. % ohne USt-IdNr. | EU (ja/nein)"
  const parseCountries = text => String(text || '')
    .split(/\r?\n/)
    .map(line => line.split('|').map(part => part.trim()))
    .filter(parts => parts.length >= 3 && /^[A-Za-z]{2}$/.test(parts[0]))
    .map(([code, name, shipping, vat, eu]) => ({
      code: code.toUpperCase(),
      name: name || code.toUpperCase(),
      shipping: parseMoney(shipping) ?? 0,
      vat: parseNumber(vat, 0),
      eu: /^(ja|yes|true|1|eu)$/i.test(String(eu || '').trim())
    }));

  // "Bis Teile | Preis | Versandart", e.g. "30 | 9,90 | DHL Express", sorted by the number of pieces.
  const parseShippingRates = text => String(text || '')
    .split(/\r?\n/)
    .map(line => line.split('|').map(part => part.trim()))
    .filter(parts => parts.length >= 2 && parseNumber(parts[0], 0) > 0 && parseMoney(parts[1]) !== null)
    .map(([upTo, price, method]) => ({ upTo: Math.round(parseNumber(upTo, 0)), price: parseMoney(price), method: method || '' }))
    .sort((a, b) => a.upTo - b.upTo);

  const AUDIENCES = ['team', 'company', 'artist', 'event', 'school', 'trade'];

  const normalizeConfig = raw => {
    const vat = parseNumber(raw.vat, 19);
    const countries = parseCountries(raw.countries);
    const home = countries[0] ? countries[0].code : 'DE';
    const products = (raw.products || [])
      .filter(product => product && product.id && Array.isArray(product.variants) && product.variants.length)
      .map(product => ({
        ...product,
        days: Math.max(1, Math.round(parseNumber(product.days, 6))),
        min: Math.max(1, Math.round(parseNumber(product.min, 1))),
        for: (product.for || []).filter(key => AUDIENCES.includes(key))
      }));
    const extras = (raw.extras || [])
      .filter(extra => extra && extra.id && extra.title)
      .map(extra => ({
        ...extra,
        unit: extra.unit === 'order' ? 'order' : 'piece',
        price: parseMoney(extra.price) ?? 0,
        tag: String(extra.tag || '').trim(),
        was: String(extra.was || '').trim(),
        tone: ['blue', 'yellow'].includes(String(extra.tone || '').trim()) ? String(extra.tone).trim() : '',
        for: (extra.for || []).filter(key => AUDIENCES.includes(key))
      }));
    const approvalDays = Math.max(0, Math.round(parseNumber(raw.approvalDays, 2)));
    const expressPercent = parseNumber(raw.expressPercent, 0);
    const datePercent = parseNumber(raw.datePercent, 0);
    return {
      ...raw,
      vat,
      home,
      countries: countries.length ? countries : [{ code: 'DE', name: 'Deutschland', shipping: 590, vat, eu: true }],
      products,
      extras,
      tiers: parseSteps(raw.tiers),
      leadTimes: parseSteps(raw.leadTimes),
      maxPieces: Math.max(0, Math.round(parseNumber(raw.maxPieces, 0))),
      approvalDays,
      // Express: a surcharge on goods and extras for a faster proof and a shorter production.
      express: raw.expressEnabled && expressPercent > 0
        ? {
          percent: expressPercent,
          days: Math.max(1, Math.round(parseNumber(raw.expressDays, 2))),
          approvalDays: Math.min(approvalDays, Math.max(0, Math.round(parseNumber(raw.expressApprovalDays, approvalDays))))
        }
        : null,
      fixedDate: datePercent > 0 ? { percent: datePercent } : null,
      design: {
        adjust: parseMoney(raw.designAdjust) ?? 0,
        service: parseMoney(raw.designService) ?? 0,
        ksk: Math.max(0, parseNumber(raw.designKsk, 0))
      },
      parcelKg: Math.max(1, parseNumber(raw.parcelKg, 20)),
      shippingRates: parseShippingRates(raw.shippingRates),
      freeShippingFrom: parseMoney(raw.freeShippingFrom),
      validDays: Math.max(1, Math.round(parseNumber(raw.validDays, 7)))
    };
  };

  const productOf = (config, id) => config.products.find(product => product.id === id) || null;
  const countryOf = (config, code) => config.countries.find(country => country.code === code) || config.countries[0];

  // The variant of a product in one color and size: [color, size, price in cents, grams, available].
  const variantOf = (product, color, size) => {
    const ci = product.colors.findIndex(entry => entry.name === color);
    const si = product.sizes.indexOf(size);
    return product.variants.find(variant => variant[0] === ci && variant[1] === si && variant[4] !== 0) || null;
  };

  const sizesFor = (product, color) => product.sizes.filter(size => variantOf(product, color, size));

  // ---------------------------------------------------------------------------------------------------------------
  // Sizes. When a customer only knows the total, it is spread the way team orders usually come in.

  const SIZE_WEIGHTS = { XXS: 2, XS: 5, S: 13, M: 24, L: 26, XL: 17, '2XL': 8, XXL: 8, '3XL': 3, '4XL': 1.5, '5XL': 0.5 };

  const distribute = (total, sizes) => {
    const count = Math.max(0, Math.round(total));
    if (!sizes.length || !count) return {};
    const weights = sizes.map(size => SIZE_WEIGHTS[size] ?? 0);
    const even = weights.every(weight => weight === 0);
    const sum = even ? sizes.length : weights.reduce((a, b) => a + b, 0);
    const shares = sizes.map((size, i) => (count * (even ? 1 : weights[i])) / sum);
    const result = shares.map(Math.floor);
    let left = count - result.reduce((a, b) => a + b, 0);
    shares
      .map((share, i) => [share - Math.floor(share), i])
      .sort((a, b) => b[0] - a[0] || a[1] - b[1])
      .forEach(([, i]) => {
        if (left > 0) {
          result[i] += 1;
          left -= 1;
        }
      });
    return Object.fromEntries(sizes.map((size, i) => [size, result[i]]).filter(([, n]) => n > 0));
  };

  // The pieces of one line by size: what was entered, or the total spread over the sizes.
  const piecesOf = (product, line) => {
    const sizes = sizesFor(product, line.color);
    if (line.total !== null && line.total !== undefined) return distribute(line.total, sizes);
    return Object.fromEntries(Object.entries(line.sizes || {})
      .filter(([size, n]) => sizes.includes(size) && n > 0)
      .map(([size, n]) => [size, Math.round(n)]));
  };

  const countOf = (product, line) => Object.values(piecesOf(product, line)).reduce((a, b) => a + b, 0);

  // ---------------------------------------------------------------------------------------------------------------
  // Tax. Shop prices are final prices with German VAT; every customer is shown net prices with the VAT added on the
  // total. Deliveries outside the EU are exports without German VAT; EU customers with a VAT ID
  // outside Germany get the intra-Community supply without VAT (the customer accounts for it).

  const taxFor = (config, state) => {
    const country = countryOf(config, state.country);
    if (!country.eu) return { rate: 0, note: 'export', country };
    if (country.code !== config.home && state.vatId) return { rate: 0, note: 'reverse', country };
    return { rate: country.code === config.home ? config.vat : country.vat, note: null, country };
  };

  // ---------------------------------------------------------------------------------------------------------------
  // The quote. Everything a customer sees on screen and in the PDF comes out of this one function.

  const quote = (config, state, today = new Date()) => {
    const tax = taxFor(config, state);
    // Configured prices are German final prices; this turns one into the net price the customer is shown.
    const toNet = homeGross => homeGross / (1 + config.vat / 100);

    const lines = (state.lines || [])
      .map(line => ({ line, product: productOf(config, line.product) }))
      .filter(({ product, line }) => product && product.colors.some(color => color.name === line.color));
    const pieces = lines.reduce((sum, { product, line }) => sum + countOf(product, line), 0);
    const [tierFrom, tierPercent] = stepFor(config.tiers, pieces);
    const upcoming = nextStep(config.tiers, pieces);
    const factor = 1 - tierPercent / 100;

    let goods = 0;
    let list = 0;
    let grams = 0;
    let homeGoods = 0;
    const rows = [];
    lines.forEach(({ product, line }) => {
      const byPrice = new Map();
      Object.entries(piecesOf(product, line)).forEach(([size, count]) => {
        const variant = variantOf(product, line.color, size);
        if (!variant || !count) return;
        grams += (variant[3] || 0) * count;
        homeGoods += variant[2] * count;
        const group = byPrice.get(variant[2]) || [];
        group.push([size, count]);
        byPrice.set(variant[2], group);
      });
      [...byPrice.entries()].sort((a, b) => a[0] - b[0]).forEach(([price, sizes]) => {
        const count = sizes.reduce((sum, [, n]) => sum + n, 0);
        const listUnit = Math.round(toNet(price));
        const unit = Math.round(toNet(price) * factor);
        goods += unit * count;
        list += listUnit * count;
        rows.push({
          product: product.id,
          title: product.title,
          color: line.color,
          sizes,
          estimated: line.total !== null && line.total !== undefined && sizesFor(product, line.color).length > 1,
          count,
          listUnit,
          unit,
          total: unit * count
        });
      });
    });

    const extras = (state.extras || [])
      .map(id => config.extras.find(extra => extra.id === id))
      .filter(Boolean)
      .map(extra => {
        const count = extra.unit === 'piece' ? pieces : 1;
        const unit = Math.round(toNet(extra.price));
        return { id: extra.id, title: extra.title, count, unit, total: unit * count, perPiece: extra.unit === 'piece' };
      })
      .filter(extra => extra.count > 0);

    const designFee = state.design === 'adjust' ? config.design.adjust : state.design === 'service' ? config.design.service : 0;
    const designNet = Math.round(toNet(designFee));
    const design = designFee > 0 ? { title: state.design === 'service' ? 'Designservice' : 'Datei-Optimierung', count: 1, unit: designNet, total: designNet } : null;
    // The artists' social levy on the design service, outside the surcharges.
    const kskTotal = design && state.design === 'service' ? Math.round((designNet * config.design.ksk) / 100) : 0;
    const ksk = kskTotal > 0 ? { title: `Künstlersozialabgabe (${formatPercent(config.design.ksk)})`, count: 1, unit: kskTotal, total: kskTotal } : null;

    const extrasTotal = extras.reduce((sum, extra) => sum + extra.total, 0) + (design ? design.total : 0);
    const express = state.express && config.express && pieces > 0
      ? { title: `Express-Produktion (+${formatPercent(config.express.percent)})`, total: Math.round(((goods + extrasTotal) * config.express.percent) / 100) }
      : null;
    const wanted = parseDate(state.date);
    const fixedDate = wanted && config.fixedDate && pieces > 0
      ? { title: `Fixtermin (+${formatPercent(config.fixedDate.percent)})`, total: Math.round(((goods + extrasTotal) * config.fixedDate.percent) / 100) }
      : null;

    // At home one shipment priced by the number of pieces, when rates are set; abroad by parcels and weight.
    const kg = grams / 1000;
    const rates = tax.country.code === config.home ? config.shippingRates : [];
    const rate = rates.length ? rates.find(entry => pieces <= entry.upTo) || rates[rates.length - 1] : null;
    const parcels = pieces > 0 ? (rate ? 1 : Math.max(1, Math.ceil(kg / config.parcelKg))) : 0;
    const freeShipping = config.freeShippingFrom !== null && config.freeShippingFrom !== undefined && homeGoods * factor >= config.freeShippingFrom;
    const shippingHome = freeShipping ? 0 : rate ? rate.price : tax.country.shipping * parcels;
    // Collected at the studio, nothing is shipped.
    const shipping = state.pickup
      ? { method: 'Abholung', pickup: true, parcels: 0, kg, free: false, total: 0 }
      : { method: rate ? rate.method : '', pickup: false, parcels, kg, free: freeShipping, total: pieces > 0 ? Math.round(toNet(shippingHome)) : 0 };

    const net = goods + extrasTotal + (ksk ? ksk.total : 0) + (express ? express.total : 0) + (fixedDate ? fixedDate.total : 0) + shipping.total;
    const vat = Math.round((net * tax.rate) / 100);
    const gross = net + vat;

    // Delivery: the slowest product plus the days a big run takes, after the customer approved the proof.
    const baseDays = lines.reduce((max, { product }) => Math.max(max, product.days), 0);
    const [, extraDays] = stepFor(config.leadTimes, pieces);
    const standardDays = baseDays + extraDays;
    const expressDays = config.express ? Math.max(2, standardDays - config.express.days) : standardDays;
    const days = express ? expressDays : standardDays;
    const standardApproval = config.approvalDays;
    const expressApproval = config.express ? config.express.approvalDays : standardApproval;
    const approvalDays = express ? expressApproval : standardApproval;
    const start = addWorkdays(today, approvalDays);
    const arrival = addWorkdays(start, days);
    let deadline = null;
    if (wanted && pieces > 0) {
      const workdays = workdaysBetween(today, wanted);
      const available = workdays - standardApproval;
      const status = available >= standardDays ? 'ok'
        : config.express && workdays - expressApproval >= expressDays ? 'express'
          : 'tight';
      deadline = { date: wanted, available, status, met: status === 'ok' || (status === 'express' && Boolean(express)) };
    }

    return {
      pieces,
      over: config.maxPieces > 0 && pieces > config.maxPieces,
      tier: { from: tierFrom, percent: tierPercent },
      next: upcoming ? { from: upcoming[0], percent: upcoming[1], missing: upcoming[0] - pieces } : null,
      rows,
      extras,
      design,
      ksk,
      express,
      fixedDate,
      shipping,
      goods,
      list,
      savings: Math.max(0, list - goods),
      net,
      vat,
      gross,
      perPiece: pieces > 0 ? Math.round(net / pieces) : 0,
      // The printed piece alone, before extras, express and shipping: the price shown while the order is put together.
      goodsPerPiece: pieces > 0 ? Math.round(goods / pieces) : 0,
      tax,
      delivery: { days, standardDays, expressDays, approvalDays, standardApproval, expressApproval, start, arrival, deadline }
    };
  };

  // ---------------------------------------------------------------------------------------------------------------
  // The shareable link. The offer travels in the address after "#", so it never reaches a server log. The customer's
  // link carries the order and, when shared, the contact details sealed (see sealContact); the shop's order link
  // carries them in the clear.
  //
  // The order is packed into a few short fields and sent as one base64url string; a version byte up front tells it
  // from the JSON links before (which still open). Shared with contact details, the seal is packed in the same string.

  const bytesToBase64Url = bytes => {
    let binary = '';
    bytes.forEach(byte => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const base64UrlToBytes = text => Uint8Array.from(atob(String(text).replace(/-/g, '+').replace(/_/g, '/')), char => char.charCodeAt(0));

  const DESIGNS = { r: 'ready', a: 'adjust', s: 'service', o: 'studio' };
  const key = (map, value) => Object.keys(map).find(k => map[k] === value);

  const CONTACT_FIELDS = ['name', 'org', 'email', 'phone', 'message'];

  // Version bytes: the order alone, or the order with its contact details sealed.
  const LINK_PLAIN = 2;
  const LINK_SEALED = 3;
  const FIELD = '~';
  // Contact fields are joined by a character nobody types.
  const JOIN = '\u001f';

  // "Weiß" -> "weiss", "Grau meliert" -> "graumeliert", "One Size" -> "onesize".
  const slug = text => String(text).toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');

  const joinFields = values => values.map(value => String(value || '').replace(/\u001f/g, ' ')).join(JOIN);

  const withVersion = (version, bytes) => {
    const result = new Uint8Array(bytes.length + 1);
    result[0] = version;
    result.set(bytes, 1);
    return result;
  };

  // Offer number, audience/design/express/VAT ID/pickup flags, wished date, country, gross total (base 36), extras and one
  // field per item, e.g. 261026K7QX~0r00~261120~DE~2n9c~neck,shop~diy-shirt,weiss,m:10,l:10~diy-hoodie,navy,20.
  // Colours and sizes go by their plain names, so a link survives a reordered catalogue. The shop's order link adds
  // the contact details in the clear.
  const encodeState = (state, { contact = false } = {}) => {
    const number = /^MXD-(\d{6})-([A-Z2-9]{4})$/.exec(state.number || '');
    const flags = [
      Math.max(0, AUDIENCES.indexOf(state.who)).toString(36),
      key(DESIGNS, state.design) || 'r',
      state.express ? 1 : 0,
      state.vatId ? 1 : 0,
      state.pickup ? 1 : 0
    ].join('');
    const lines = (state.lines || []).map(line => [
      line.product,
      slug(line.color),
      ...(line.total !== null && line.total !== undefined
        ? [line.total]
        : Object.entries(line.sizes || {}).map(([size, count]) => `${slug(size)}:${count}`))
    ].join(','));
    const order = [
      number ? number[1] + number[2] : '',
      flags,
      parseDate(state.date) ? state.date.slice(2).replace(/-/g, '') : '',
      state.country || '',
      Number.isFinite(state.total) ? state.total.toString(36) : '',
      (state.extras || []).join(','),
      ...lines
    ].join(FIELD);
    const text = contact ? `${order}${JOIN}${joinFields([...CONTACT_FIELDS.map(field => (state.contact || {})[field]), state.vatId])}` : order;
    return bytesToBase64Url(withVersion(LINK_PLAIN, new TextEncoder().encode(text)));
  };

  // Contact details for a shared link, sealed with AES-128-GCM under a fresh random key that travels with them: key
  // and ciphertext in one base64url string. Whoever holds the link can open them, no server ever sees them, and a link
  // changed by a single character opens nothing. Each key seals once, so its nonce can stay zero.
  const SEAL_KEY_BYTES = 16;
  const SEAL_NONCE = new Uint8Array(12);

  const sealContact = async contact => {
    const raw = crypto.getRandomValues(new Uint8Array(SEAL_KEY_BYTES));
    const sealKey = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt']);
    const plain = new TextEncoder().encode(joinFields(CONTACT_FIELDS.map(field => (contact || {})[field])));
    const sealed = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: SEAL_NONCE }, sealKey, plain));
    const bytes = new Uint8Array(raw.length + sealed.length);
    bytes.set(raw);
    bytes.set(sealed, raw.length);
    return bytesToBase64Url(bytes);
  };

  // Seals from before: a 256-bit key, a random nonce and the fields as JSON.
  const openLegacy = async bytes => {
    const sealKey = await crypto.subtle.importKey('raw', bytes.slice(0, 32), 'AES-GCM', false, ['decrypt']);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bytes.slice(32, 44) }, sealKey, bytes.slice(44));
    return JSON.parse(new TextDecoder().decode(plain));
  };

  // The contact details back from sealContact, or null when the text is not one of its seals.
  const openContact = async text => {
    try {
      const bytes = base64UrlToBytes(text);
      let values;
      try {
        const sealKey = await crypto.subtle.importKey('raw', bytes.slice(0, SEAL_KEY_BYTES), 'AES-GCM', false, ['decrypt']);
        values = new TextDecoder().decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: SEAL_NONCE }, sealKey, bytes.slice(SEAL_KEY_BYTES))).split(JOIN);
      } catch {
        values = await openLegacy(bytes);
      }
      if (!Array.isArray(values)) return null;
      return Object.fromEntries(CONTACT_FIELDS.map((field, index) => [field, typeof values[index] === 'string' ? values[index].slice(0, 2000) : '']));
    } catch {
      return null;
    }
  };

  // The order and a seal from sealContact in one string, so a shared link stays in one piece.
  const joinShared = (code, sealed) => {
    if (!sealed) return code;
    const order = base64UrlToBytes(code).slice(1);
    const seal = base64UrlToBytes(sealed);
    const bytes = new Uint8Array(3 + order.length + seal.length);
    bytes.set([LINK_SEALED, order.length >> 8, order.length & 255]);
    bytes.set(order, 3);
    bytes.set(seal, 3 + order.length);
    return bytesToBase64Url(bytes);
  };

  // Back into [order, seal]; links from before carried the seal after a dot.
  const splitShared = text => {
    if (String(text).includes('.')) return String(text).split('.');
    try {
      const bytes = base64UrlToBytes(text);
      if (bytes[0] !== LINK_SEALED) return [text, ''];
      const end = 3 + ((bytes[1] << 8) | bytes[2]);
      return [bytesToBase64Url(withVersion(LINK_PLAIN, bytes.slice(3, end))), bytesToBase64Url(bytes.slice(end))];
    } catch {
      return [text, ''];
    }
  };

  // The fields of a compact link in the shape of a JSON link, colours and sizes back to the catalogue's names.
  const compactData = (config, text) => {
    const [order, ...known] = text.split(JOIN);
    const [number, flags = '', date, country, total, extras, ...items] = order.split(FIELD);
    return {
      v: 1,
      n: number ? `MXD-${number.slice(0, 6)}-${number.slice(6)}` : '',
      o: number ? `20${number.slice(0, 2)}-${number.slice(2, 4)}-${number.slice(4, 6)}` : '',
      w: AUDIENCES[parseInt(flags[0], 36)],
      d: flags[1],
      e: flags[2] === '1' ? 1 : 0,
      r: flags[3] === '1' ? 1 : 0,
      p: flags[4] === '1' ? 1 : 0,
      dt: date ? `20${date.slice(0, 2)}-${date.slice(2, 4)}-${date.slice(4, 6)}` : '',
      c: country,
      g: total ? parseInt(total, 36) : null,
      x: extras ? extras.split(',') : [],
      l: items.map(item => {
        const [id, color, ...amounts] = item.split(',');
        const product = productOf(config, id);
        if (!product) return null;
        const colour = product.colors.find(entry => slug(entry.name) === color);
        if (amounts.length === 1 && /^\d+$/.test(amounts[0])) return [id, colour && colour.name, Number(amounts[0])];
        return [id, colour && colour.name, Object.fromEntries(amounts.map(amount => {
          const [size, count] = amount.split(':');
          return [product.sizes.find(entry => slug(entry) === size), Number(count)];
        }).filter(([size]) => size))];
      }).filter(Boolean),
      ...(known.length ? { k: known } : {})
    };
  };

  // Reads a link back into a state; anything the shop no longer sells is dropped.
  const decodeState = (config, text) => {
    let data;
    try {
      const bytes = base64UrlToBytes(decodeURIComponent(String(text)));
      data = bytes[0] === LINK_PLAIN ? compactData(config, new TextDecoder().decode(bytes.slice(1))) : JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      return null;
    }
    return stateFrom(config, data);
  };

  // The readable link for people and agents, e.g.
  // ?for=team&item=diy-shirt,Weiß,S:6,M:10&item=diy-hoodie,Schwarz,20&design=ready&extras=neck&express=1&date=2026-11-20
  // Each item is a product, a colour and either size:amount pairs or one total; names match regardless of case.
  const queryState = (config, search) => {
    const params = new URLSearchParams(search);
    const items = params.getAll('item');
    if (!items.length) return null;
    const same = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
    const lines = items.map(item => {
      const [id = '', color = '', ...amounts] = item.split(',').map(part => part.trim());
      const product = config.products.find(entry => same(entry.id, id));
      if (!product) return null;
      const colour = product.colors.find(entry => same(entry.name, color)) || (color ? null : product.colors[0]);
      if (!colour) return null;
      if (amounts.length === 1 && /^\d+$/.test(amounts[0])) return [product.id, colour.name, Number(amounts[0])];
      return [product.id, colour.name, Object.fromEntries(amounts
        .map(amount => amount.split(':').map(part => part.trim()))
        .map(([size, count]) => [product.sizes.find(entry => same(entry, size)), Number(count)])
        .filter(([size, count]) => size && Number.isInteger(count)))];
    }).filter(Boolean);
    const extras = (params.get('extras') || '').split(',').map(id => id.trim()).filter(Boolean);
    return stateFrom(config, {
      v: 1,
      w: params.get('for'),
      l: lines,
      d: key(DESIGNS, params.get('design')),
      x: config.extras.filter(extra => extras.some(id => same(id, extra.id))).map(extra => extra.id),
      e: params.get('express') === '1' ? 1 : 0,
      dt: params.get('date') || ''
    });
  };

  const stateFrom = (config, data) => {
    if (!data || data.v !== 1 || !Array.isArray(data.l)) return null;
    const lines = data.l
      .filter(entry => Array.isArray(entry) && entry.length === 3)
      .map(([product, color, amount]) => {
        const found = productOf(config, product);
        if (!found || !found.colors.some(entry => entry.name === color)) return null;
        if (typeof amount === 'number') return { product, color, total: Math.max(0, Math.round(amount)), sizes: null };
        const sizes = Object.fromEntries(Object.entries(amount || {})
          .filter(([size, n]) => found.sizes.includes(size) && Number.isFinite(n) && n > 0)
          .map(([size, n]) => [size, Math.round(n)]));
        return { product, color, total: null, sizes };
      })
      .filter(Boolean);
    if (!lines.length) return null;
    const known = Array.isArray(data.k) ? data.k.map(value => (typeof value === 'string' ? value.slice(0, 2000) : '')) : null;
    return {
      number: typeof data.n === 'string' ? data.n.slice(0, 24) : '',
      created: parseDate(data.o) ? data.o : '',
      who: AUDIENCES.includes(data.w) ? data.w : 'company',
      lines,
      design: DESIGNS[data.d] || 'ready',
      extras: Array.isArray(data.x) ? data.x.filter(id => config.extras.some(extra => extra.id === id)) : [],
      express: data.e === 1 && Boolean(config.express),
      pickup: data.p === 1,
      date: parseDate(data.dt) ? data.dt : '',
      country: config.countries.some(country => country.code === data.c) ? data.c : config.home,
      vatId: known && known[CONTACT_FIELDS.length] ? known[CONTACT_FIELDS.length] : data.r === 1 ? '(angegeben)' : '',
      total: Number.isFinite(data.g) ? data.g : null,
      ...(known ? { contact: Object.fromEntries(CONTACT_FIELDS.map((field, index) => [field, known[index] || ''])) } : {})
    };
  };

  // "MXD-251023-K7QX": the date plus four characters nobody mixes up when reading them out on the phone.
  const offerNumber = (date = new Date(), random = () => Math.random()) => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let tail = '';
    for (let i = 0; i < 4; i += 1) tail += alphabet[Math.floor(random() * alphabet.length) % alphabet.length];
    return `MXD-${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${tail}`;
  };

  // ---------------------------------------------------------------------------------------------------------------
  // QR codes (ISO/IEC 18004, byte mode). Written along the lines of Project Nayuki's reference implementation.

  const QR_LEVELS = { L: [0, 1], M: [1, 0], Q: [2, 3], H: [3, 2] };
  const QR_ECC_PER_BLOCK = [
    [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
    [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
    [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
    [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30]
  ];
  const QR_BLOCKS = [
    [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
    [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
    [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
    [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81]
  ];

  const qrRawModules = version => {
    let result = (16 * version + 128) * version + 64;
    if (version >= 2) {
      const align = Math.floor(version / 7) + 2;
      result -= (25 * align - 10) * align - 55;
      if (version >= 7) result -= 36;
    }
    return result;
  };

  const qrDataCodewords = (version, level) => Math.floor(qrRawModules(version) / 8) - QR_ECC_PER_BLOCK[level][version] * QR_BLOCKS[level][version];

  const gfMultiply = (x, y) => {
    let z = 0;
    for (let i = 7; i >= 0; i -= 1) {
      z = (z << 1) ^ ((z >>> 7) * 0x11d);
      z ^= ((y >>> i) & 1) * x;
    }
    return z;
  };

  const rsDivisor = degree => {
    const result = new Array(degree).fill(0);
    result[degree - 1] = 1;
    let power = 1;
    for (let i = 0; i < degree; i += 1) {
      for (let j = 0; j < result.length; j += 1) {
        result[j] = gfMultiply(result[j], power);
        if (j + 1 < result.length) result[j] ^= result[j + 1];
      }
      power = gfMultiply(power, 0x02);
    }
    return result;
  };

  const rsRemainder = (data, divisor) => {
    const result = divisor.map(() => 0);
    data.forEach(byte => {
      const factor = byte ^ result.shift();
      result.push(0);
      divisor.forEach((coefficient, i) => {
        result[i] ^= gfMultiply(coefficient, factor);
      });
    });
    return result;
  };

  const qrAlignment = version => {
    if (version === 1) return [];
    const count = Math.floor(version / 7) + 2;
    const step = Math.floor((version * 8 + count * 3 + 5) / (count * 4 - 4)) * 2;
    const result = [6];
    for (let pos = version * 4 + 17 - 7; result.length < count; pos -= step) result.splice(1, 0, pos);
    return result;
  };

  const QR_MASKS = [
    (x, y) => (x + y) % 2 === 0,
    (x, y) => y % 2 === 0,
    x => x % 3 === 0,
    (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
    (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
    (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
  ];

  // Encodes text into a QR code; returns { size, modules } with modules[y][x] true for dark.
  const qr = (text, levelName = 'M') => {
    const [level, formatBits] = QR_LEVELS[levelName] || QR_LEVELS.M;
    const bytes = Array.from(new TextEncoder().encode(String(text)));
    let version = 1;
    for (; version <= 40; version += 1) {
      const countBits = version < 10 ? 8 : 16;
      if (4 + countBits + bytes.length * 8 <= qrDataCodewords(version, level) * 8) break;
    }
    if (version > 40) throw new Error('Text too long for a QR code');

    // Data bits: byte mode, length, the bytes, terminator and padding.
    const bits = [];
    const push = (value, length) => {
      for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
    };
    push(4, 4);
    push(bytes.length, version < 10 ? 8 : 16);
    bytes.forEach(byte => push(byte, 8));
    const capacity = qrDataCodewords(version, level) * 8;
    push(0, Math.min(4, capacity - bits.length));
    push(0, (8 - (bits.length % 8)) % 8);
    for (let filler = 0xec; bits.length < capacity; filler ^= 0xec ^ 0x11) push(filler, 8);
    const data = [];
    for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));

    // Error correction per block, then the blocks interleaved.
    const blocks = QR_BLOCKS[level][version];
    const eccLength = QR_ECC_PER_BLOCK[level][version];
    const raw = Math.floor(qrRawModules(version) / 8);
    const shortBlocks = blocks - (raw % blocks);
    const shortLength = Math.floor(raw / blocks);
    const divisor = rsDivisor(eccLength);
    const parts = [];
    for (let i = 0, k = 0; i < blocks; i += 1) {
      const chunk = data.slice(k, k + shortLength - eccLength + (i < shortBlocks ? 0 : 1));
      k += chunk.length;
      const ecc = rsRemainder(chunk, divisor);
      if (i < shortBlocks) chunk.push(0);
      parts.push(chunk.concat(ecc));
    }
    const codewords = [];
    for (let i = 0; i < parts[0].length; i += 1) {
      parts.forEach((part, j) => {
        if (i !== shortLength - eccLength || j >= shortBlocks) codewords.push(part[i]);
      });
    }

    const size = version * 4 + 17;
    const modules = Array.from({ length: size }, () => new Array(size).fill(false));
    const fixed = Array.from({ length: size }, () => new Array(size).fill(false));
    const set = (x, y, dark) => {
      modules[y][x] = dark;
      fixed[y][x] = true;
    };

    for (let i = 0; i < size; i += 1) {
      set(6, i, i % 2 === 0);
      set(i, 6, i % 2 === 0);
    }
    const finder = (cx, cy) => {
      for (let dy = -4; dy <= 4; dy += 1) {
        for (let dx = -4; dx <= 4; dx += 1) {
          const x = cx + dx;
          const y = cy + dy;
          const dist = Math.max(Math.abs(dx), Math.abs(dy));
          if (x >= 0 && x < size && y >= 0 && y < size) set(x, y, dist !== 2 && dist !== 4);
        }
      }
    };
    finder(3, 3);
    finder(size - 4, 3);
    finder(3, size - 4);
    const align = qrAlignment(version);
    align.forEach((ay, i) => align.forEach((ax, j) => {
      const corner = (i === 0 && j === 0) || (i === 0 && j === align.length - 1) || (i === align.length - 1 && j === 0);
      if (corner) return;
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }));

    const drawFormat = mask => {
      const value = (formatBits << 3) | mask;
      let rem = value;
      for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
      const format = ((value << 10) | rem) ^ 0x5412;
      const bit = i => ((format >>> i) & 1) === 1;
      for (let i = 0; i <= 5; i += 1) set(8, i, bit(i));
      set(8, 7, bit(6));
      set(8, 8, bit(7));
      set(7, 8, bit(8));
      for (let i = 9; i < 15; i += 1) set(14 - i, 8, bit(i));
      for (let i = 0; i < 8; i += 1) set(size - 1 - i, 8, bit(i));
      for (let i = 8; i < 15; i += 1) set(8, size - 15 + i, bit(i));
      set(8, size - 8, true);
    };
    drawFormat(0);
    if (version >= 7) {
      let rem = version;
      for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
      const value = (version << 12) | rem;
      for (let i = 0; i < 18; i += 1) {
        const dark = ((value >>> i) & 1) === 1;
        const a = size - 11 + (i % 3);
        const b = Math.floor(i / 3);
        set(a, b, dark);
        set(b, a, dark);
      }
    }

    let index = 0;
    for (let right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < size; vert += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? size - 1 - vert : vert;
          if (!fixed[y][x] && index < codewords.length * 8) {
            modules[y][x] = ((codewords[index >>> 3] >>> (7 - (index & 7))) & 1) === 1;
            index += 1;
          }
        }
      }
    }

    const applyMask = mask => {
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          if (!fixed[y][x] && QR_MASKS[mask](x, y)) modules[y][x] = !modules[y][x];
        }
      }
    };

    // Penalty rules of the standard, to pick the mask that scans best.
    const penalty = () => {
      let score = 0;
      const lineScore = cells => {
        let result = 0;
        let run = 1;
        for (let i = 1; i <= cells.length; i += 1) {
          if (i < cells.length && cells[i] === cells[i - 1]) {
            run += 1;
          } else {
            if (run >= 5) result += run - 2;
            run = 1;
          }
        }
        const pattern = cells.map(cell => (cell ? '1' : '0')).join('');
        result += (pattern.match(/(?=(00001011101|10111010000))/g) || []).length * 40;
        return result;
      };
      for (let i = 0; i < size; i += 1) {
        score += lineScore(modules[i]);
        score += lineScore(modules.map(row => row[i]));
      }
      let dark = 0;
      for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
          if (modules[y][x]) dark += 1;
          if (x < size - 1 && y < size - 1) {
            const c = modules[y][x];
            if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) score += 3;
          }
        }
      }
      const total = size * size;
      score += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
      return score;
    };

    let best = 0;
    let bestScore = Infinity;
    for (let mask = 0; mask < 8; mask += 1) {
      applyMask(mask);
      drawFormat(mask);
      const score = penalty();
      if (score < bestScore) {
        best = mask;
        bestScore = score;
      }
      applyMask(mask);
    }
    applyMask(best);
    drawFormat(best);
    return { size, version, modules };
  };

  // The code as an SVG path in module units, for the screen.
  const qrPath = code => {
    let path = '';
    code.modules.forEach((row, y) => row.forEach((dark, x) => {
      if (dark) path += `M${x} ${y}h1v1h-1z`;
    }));
    return path;
  };

  // ---------------------------------------------------------------------------------------------------------------
  // PDF. A small writer for what an offer needs: the PDF standard fonts Helvetica and Helvetica-Bold (WinAnsi),
  // lines, boxes, a JPEG logo, links and the QR code as vector squares. Measurements are in millimetres from the
  // top left of an A4 page.

  // Glyph widths (1/1000 em) of Helvetica and Helvetica-Bold for the WinAnsi codes 32–255.
  const WIDTHS = {
    regular: [278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584, 0, 556, 0, 222, 556, 333, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0, 0, 222, 222, 333, 333, 350, 556, 1000, 333, 1000, 500, 333, 944, 0, 500, 667, 278, 333, 556, 556, 556, 556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333, 333, 556, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611, 667, 667, 667, 667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611, 556, 556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278, 556, 556, 556, 556, 556, 556, 556, 584, 611, 556, 556, 556, 556, 500, 556, 500],
    bold: [278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584, 0, 556, 0, 278, 556, 500, 1000, 556, 556, 333, 1000, 667, 333, 1000, 0, 611, 0, 0, 278, 278, 500, 500, 350, 556, 1000, 333, 1000, 556, 333, 944, 0, 500, 667, 278, 333, 556, 556, 556, 556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333, 333, 611, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611, 722, 722, 722, 722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611, 556, 556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278, 611, 611, 611, 611, 611, 611, 611, 584, 611, 611, 611, 611, 611, 556, 611, 556]
  };

  // Characters outside Latin-1 that WinAnsi still has.
  const WIN_ANSI = { '€': 0x80, '‚': 0x82, 'ƒ': 0x83, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87, 'ˆ': 0x88, '‰': 0x89, 'Š': 0x8a, '‹': 0x8b, 'Œ': 0x8c, 'Ž': 0x8e, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97, '˜': 0x98, '™': 0x99, 'š': 0x9a, '›': 0x9b, 'œ': 0x9c, 'ž': 0x9e, 'Ÿ': 0x9f, '−': 0x96, '→': 0x2d, ' ': 0x20, ' ': 0x20 };

  const winAnsi = text => Array.from(String(text), char => {
    if (WIN_ANSI[char] !== undefined) return WIN_ANSI[char];
    const code = char.codePointAt(0);
    if (code >= 32 && code < 127) return code;
    if (code >= 0xa0 && code <= 0xff) return code;
    return 0x3f;
  });

  const MM = 72 / 25.4;

  const createPdf = ({ title = '', author = '', subject = '' } = {}) => {
    const W = 210;
    const H = 297;
    const pages = [];
    let page = null;
    let image = null;

    const num = value => {
      const rounded = Math.round(value * 1000) / 1000;
      return Object.is(rounded, -0) ? '0' : String(rounded);
    };
    const color = (hex = '#000000') => {
      const value = Number.parseInt(String(hex).replace('#', ''), 16);
      return [(value >> 16) & 255, (value >> 8) & 255, value & 255].map(c => num(c / 255)).join(' ');
    };

    const width = (text, size, bold = false, scale = 100) => {
      const table = bold ? WIDTHS.bold : WIDTHS.regular;
      const units = winAnsi(text).reduce((sum, code) => sum + (table[code - 32] || 556), 0);
      return ((units * size) / 1000 / MM) * (scale / 100);
    };

    // Splits text into lines no wider than `max` millimetres.
    const wrap = (text, max, size, bold = false) => String(text).split('\n').flatMap(paragraph => {
      const words = paragraph.split(/\s+/).filter(Boolean);
      const lines = [];
      let current = '';
      words.forEach(word => {
        const candidate = current ? `${current} ${word}` : word;
        if (width(candidate, size, bold) <= max || !current) {
          current = candidate;
        } else {
          lines.push(current);
          current = word;
        }
      });
      lines.push(current);
      return lines;
    });

    const addPage = () => {
      page = { ops: [], links: [] };
      pages.push(page);
      return page;
    };

    const text = (value, x, y, { size = 10, bold = false, color: fill = '#000000', align = 'left', scale = 100 } = {}) => {
      const content = winAnsi(value).map(code => {
        const char = String.fromCharCode(code);
        return char === '(' || char === ')' || char === '\\' ? `\\${char}` : char;
      }).join('');
      const w = width(value, size, bold, scale);
      const left = align === 'right' ? x - w : align === 'center' ? x - w / 2 : x;
      page.ops.push(`BT /${bold ? 'F2' : 'F1'} ${num(size)} Tf ${num(scale)} Tz ${color(fill)} rg 1 0 0 1 ${num(left * MM)} ${num((H - y) * MM)} Tm (${content}) Tj ET`);
      return w;
    };

    const rect = (x, y, w, h, { fill = null, stroke = null, lineWidth = 0.25 } = {}) => {
      const shape = `${num(x * MM)} ${num((H - y - h) * MM)} ${num(w * MM)} ${num(h * MM)} re`;
      if (fill && stroke) page.ops.push(`${color(fill)} rg ${color(stroke)} RG ${num(lineWidth * MM)} w ${shape} B`);
      else if (fill) page.ops.push(`${color(fill)} rg ${shape} f`);
      else page.ops.push(`${color(stroke || '#000000')} RG ${num(lineWidth * MM)} w ${shape} S`);
    };

    const line = (x1, y1, x2, y2, { width: lineWidth = 0.25, color: stroke = '#000000' } = {}) => {
      page.ops.push(`${color(stroke)} RG ${num(lineWidth * MM)} w ${num(x1 * MM)} ${num((H - y1) * MM)} m ${num(x2 * MM)} ${num((H - y2) * MM)} l S`);
    };

    // The QR code as filled squares, `size` millimetres wide without the quiet zone.
    const qrCode = (code, x, y, size) => {
      const unit = size / code.size;
      let shapes = '';
      code.modules.forEach((row, my) => row.forEach((dark, mx) => {
        if (dark) shapes += `${num((x + mx * unit) * MM)} ${num((H - y - (my + 1) * unit) * MM)} ${num(unit * MM + 0.01)} ${num(unit * MM + 0.01)} re `;
      }));
      page.ops.push(`0 0 0 rg ${shapes}f`);
    };

    // A baseline JPEG (bytes), e.g. from canvas.toDataURL('image/jpeg'), drawn into a box.
    const jpeg = (bytes, pixelWidth, pixelHeight, x, y, w, h) => {
      image = { bytes, pixelWidth, pixelHeight };
      page.ops.push(`q ${num(w * MM)} 0 0 ${num(h * MM)} ${num(x * MM)} ${num((H - y - h) * MM)} cm /Im1 Do Q`);
    };

    const link = (x, y, w, h, url) => {
      page.links.push({ rect: [x * MM, (H - y - h) * MM, (x + w) * MM, (H - y) * MM], url });
    };

    const pdfString = value => {
      const ascii = /^[\x20-\x7e]*$/.test(value);
      if (ascii) return `(${value.replace(/[()\\]/g, char => `\\${char}`)})`;
      let hex = 'FEFF';
      for (const char of String(value)) {
        const code = char.codePointAt(0);
        if (code > 0xffff) {
          const high = Math.floor((code - 0x10000) / 0x400) + 0xd800;
          const low = ((code - 0x10000) % 0x400) + 0xdc00;
          hex += high.toString(16).padStart(4, '0') + low.toString(16).padStart(4, '0');
        } else {
          hex += code.toString(16).padStart(4, '0');
        }
      }
      return `<${hex.toUpperCase()}>`;
    };

    const output = (now = new Date()) => {
      const chunks = [];
      const offsets = [];
      let length = 0;
      const bytesOf = value => (typeof value === 'string' ? Uint8Array.from(value, char => char.charCodeAt(0) & 255) : value);
      const write = value => {
        const bytes = bytesOf(value);
        chunks.push(bytes);
        length += bytes.length;
      };
      const objects = [];
      const reserve = () => {
        objects.push(null);
        return objects.length;
      };
      const define = (id, body) => {
        objects[id - 1] = body;
      };

      const catalog = reserve();
      const pagesId = reserve();
      const regular = reserve();
      const bold = reserve();
      const info = reserve();
      const imageId = image ? reserve() : null;
      define(regular, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
      define(bold, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
      if (image) {
        define(imageId, [`<< /Type /XObject /Subtype /Image /Width ${image.pixelWidth} /Height ${image.pixelHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.bytes.length} >>\nstream\n`, image.bytes, '\nendstream']);
      }
      const stamp = `D:${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      define(info, `<< /Title ${pdfString(title)} /Author ${pdfString(author)} /Subject ${pdfString(subject)} /Creator (MIXEDOWN Angebotsrechner) /Producer (MIXEDOWN) /CreationDate (${stamp}) >>`);

      const pageIds = pages.map(entry => {
        const pageId = reserve();
        const contentId = reserve();
        const linkIds = entry.links.map(() => reserve());
        const content = entry.ops.join('\n');
        define(contentId, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
        entry.links.forEach((item, i) => {
          define(linkIds[i], `<< /Type /Annot /Subtype /Link /Rect [${item.rect.map(num).join(' ')}] /Border [0 0 0] /A << /S /URI /URI ${pdfString(item.url)} >> >>`);
        });
        const xobject = image ? ` /XObject << /Im1 ${imageId} 0 R >>` : '';
        const annots = linkIds.length ? ` /Annots [${linkIds.map(id => `${id} 0 R`).join(' ')}]` : '';
        define(pageId, `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${num(W * MM)} ${num(H * MM)}] /Resources << /Font << /F1 ${regular} 0 R /F2 ${bold} 0 R >>${xobject} >> /Contents ${contentId} 0 R${annots} >>`);
        return pageId;
      });
      define(pagesId, `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`);
      define(catalog, `<< /Type /Catalog /Pages ${pagesId} 0 R /Lang (de-DE) >>`);

      write('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n');
      objects.forEach((body, i) => {
        offsets.push(length);
        write(`${i + 1} 0 obj\n`);
        (Array.isArray(body) ? body : [body]).forEach(write);
        write('\nendobj\n');
      });
      const xref = length;
      write(`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`);
      offsets.forEach(offset => write(`${String(offset).padStart(10, '0')} 00000 n \n`));
      write(`trailer\n<< /Size ${objects.length + 1} /Root ${catalog} 0 R /Info ${info} 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

      const result = new Uint8Array(length);
      let at = 0;
      chunks.forEach(chunk => {
        result.set(chunk, at);
        at += chunk.length;
      });
      return result;
    };

    return { W, H, addPage, text, width, wrap, rect, line, qrCode, jpeg, link, output, get pageCount() { return pages.length; } };
  };

  root.MixedownOffer = {
    parseMoney,
    formatMoney,
    formatCount,
    formatPercent,
    formatDate,
    isoDate,
    parseDate,
    addWorkdays,
    addDays,
    parseSteps,
    stepFor,
    nextStep,
    normalizeConfig,
    productOf,
    countryOf,
    sizesFor,
    distribute,
    piecesOf,
    countOf,
    quote,
    encodeState,
    decodeState,
    sealContact,
    openContact,
    joinShared,
    splitShared,
    queryState,
    offerNumber,
    qr,
    qrPath,
    createPdf
  };
})(typeof window !== 'undefined' ? window : globalThis);
